import { apiGet, apiPost } from "./fetch";

/** Who a release-notes folder was written for. */
export type ReleaseNotesAudience = "staff" | "all";

/** One published release-notes folder, as the running image carries it. */
export interface ReleaseNote {
	/** The folder name under `releasenotes/`, e.g. `2026-08-25-release-notes`. */
	folder: string;
	/** When the deploy that carried it went out; null for a folder that
	 * shipped before deploys were recorded. Such a folder never badges. */
	published_at: string | null;
	/** The workflow ticket that authored it; null for a manual release. */
	ticket: number | null;
	audience: ReleaseNotesAudience;
	content_en: string;
	content_sv: string;
	/** Image filenames the markdown references, served by `releaseNoteImageUrl`. */
	images: string[];
}

export interface ReleaseNotesPayload {
	notes: ReleaseNote[];
	/** When this reader last opened the page; null if they never have. */
	seen_at: string | null;
}

export function fetchReleaseNotes(): Promise<ReleaseNotesPayload> {
	return apiGet("/api/v2/release-notes/") as Promise<ReleaseNotesPayload>;
}

/** Stamp the reader's watermark — what clears the menu badge. */
export function markReleaseNotesSeen(): Promise<{ seen_at: string }> {
	return apiPost("/api/v2/release-notes/mark_seen/", {}) as Promise<{
		seen_at: string;
	}>;
}

/** The authenticated endpoint serving one image from a published folder. */
export function releaseNoteImageUrl(folder: string, filename: string): string {
	return `/api/v2/release-notes/${folder}/images/${filename}/`;
}

/**
 * Ask how many published notes the reader has not opened the page since.
 *
 * The one integer the menu badge needs, asked for on every app load. It used
 * to be counted here from the full list, which reads every folder's markdown
 * off disk and sends the whole corpus back in both languages; the server now
 * counts publication rows against the watermark and sends the number.
 */
export function fetchUnseenReleaseNotesCount(): Promise<{ count: number }> {
	return apiGet("/api/v2/release-notes/unseen_count/") as Promise<{
		count: number;
	}>;
}
